import { all, call, put, takeEvery, takeLatest } from 'redux-saga/effects'
import {
  AdminBulkApprovePayments,
  AdminBulkApprovePaymentsProgress,
  AdminFetchPendingPaymentDetails,
  AdminFetchPendingPayments,
  AdminReviewPayment,
} from 'src/constants/actionTypes'
import { Endpoints } from 'src/constants/endpoints'
import { PaymentReviewStatus } from 'src/constants/payments-ops/enums'
import type {
  BulkApproveFailure,
  BulkApprovePaymentsPayload,
  BulkApproveResult,
  FetchPendingPaymentDetailsPayload,
  PendingPaymentDetails,
  PendingPaymentsResponse,
  ReviewPaymentPayload,
} from 'src/interfaces/paymentsReview'
import { resolveErrorMessage } from 'src/utils/sagaErrorMessage'
import { captureSentryError } from 'src/utils/sentry'
import type { AsyncAction } from 'src/utils/reduxUtils'
import { callSecureApi } from './api/callApi'

export function* onFetchPendingPayments(action: AsyncAction<undefined>) {
  try {
    // No query: the backend accepts `partnerId`/`partnerDriverId` and silently ignores both, so
    // filtering is client-side (`utils/paymentsOps/pendingPayments.ts`).
    const { payload }: { payload: PendingPaymentsResponse } = yield call(
      callSecureApi,
      Endpoints.AdminFetchPendingPayments,
      { method: 'GET' },
    )

    const items = payload?.items ?? []

    yield put(AdminFetchPendingPayments.success(items))

    if (action.next) {
      action.next(null, items)
    }
  } catch (error: unknown) {
    captureSentryError(error, { message: '[saga.paymentsReview.onFetchPendingPayments] error' })

    yield put(AdminFetchPendingPayments.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to load pending payments'))
    }
  }
}

export function* onFetchPendingPaymentDetails(
  action: AsyncAction<FetchPendingPaymentDetailsPayload>,
) {
  try {
    const { payload }: { payload: PendingPaymentDetails } = yield call(
      callSecureApi,
      Endpoints.AdminFetchPendingPaymentDetails,
      {
        method: 'GET',
        params: [{ field: ':earningId', value: action.payload.earningId }],
      },
    )

    yield put(AdminFetchPendingPaymentDetails.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (error: unknown) {
    captureSentryError(error, {
      message: '[saga.paymentsReview.onFetchPendingPaymentDetails] error',
    })

    yield put(AdminFetchPendingPaymentDetails.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to load the payment'))
    }
  }
}

export function* onReviewPayment(action: AsyncAction<ReviewPaymentPayload>) {
  const { earningId, reviewStatus, reviewNotes } = action.payload

  try {
    // `earningId` is the path param. The strict DTO 400s on an `earningId` key in the body.
    const { payload } = yield call(callSecureApi, Endpoints.AdminReviewPayment, {
      method: 'POST',
      params: [{ field: ':earningId', value: earningId }],
      body: { reviewStatus, reviewNotes },
    })

    yield put(AdminReviewPayment.success(payload))
    yield put(AdminFetchPendingPayments.request())

    if (action.next) {
      action.next(null, payload ?? true)
    }
  } catch (error: unknown) {
    captureSentryError(error, { message: '[saga.paymentsReview.onReviewPayment] error' })

    yield put(AdminReviewPayment.failed(error))
    // Approve can 500 after the approval was saved (`triggerPaymentAfterReview` runs after the
    // transaction commits). Refresh so the row disappears if it did go through.
    yield put(AdminFetchPendingPayments.request())

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Review failed — refresh before retrying'))
    }
  }
}

/**
 * Approvals in flight at once. Enough to cut a large batch from minutes to well under one, few
 * enough not to pile up `triggerPaymentAfterReview` runs on the backend.
 */
export const BULK_APPROVE_CONCURRENCY = 4

interface BulkApproveOutcome {
  earningId: string
  /** `null` = approved. */
  errorMessage: string | null
}

/** One approval of a batch. Never throws: a failure is reported, not allowed to stop the batch. */
export function* approveEarningInBatch(earningId: string, reviewNotes: string) {
  try {
    yield call(callSecureApi, Endpoints.AdminReviewPayment, {
      method: 'POST',
      params: [{ field: ':earningId', value: earningId }],
      body: { reviewStatus: PaymentReviewStatus.ManuallyApproved, reviewNotes },
    })

    const outcome: BulkApproveOutcome = { earningId, errorMessage: null }

    return outcome
  } catch (error: unknown) {
    captureSentryError(error, {
      message: '[saga.paymentsReview.onBulkApprovePayments] item error',
      data: { earningId },
    })

    const outcome: BulkApproveOutcome = {
      earningId,
      errorMessage: resolveErrorMessage(error, 'Approval failed'),
    }

    return outcome
  }
}

export function* onBulkApprovePayments(action: AsyncAction<BulkApprovePaymentsPayload>) {
  const { earningIds, reviewNotes } = action.payload
  const approved: string[] = []
  const failed: BulkApproveFailure[] = []

  try {
    // No bulk endpoint exists (`payments_review_bulk_approve` has no route), so single reviews
    // run in small concurrent chunks. One failure must not stop the batch — each is collected for
    // the results view — and progress is reported after every chunk.
    for (let start = 0; start < earningIds.length; start += BULK_APPROVE_CONCURRENCY) {
      const chunk = earningIds.slice(start, start + BULK_APPROVE_CONCURRENCY)
      const outcomes: BulkApproveOutcome[] = yield all(
        chunk.map((earningId) => {
          return call(approveEarningInBatch, earningId, reviewNotes)
        }),
      )

      outcomes.forEach((outcome) => {
        if (outcome.errorMessage === null) {
          approved.push(outcome.earningId)

          return
        }

        failed.push({ earningId: outcome.earningId, message: outcome.errorMessage })
      })

      yield put(
        AdminBulkApprovePaymentsProgress({
          done: start + chunk.length,
          total: earningIds.length,
        }),
      )
    }

    const result: BulkApproveResult = { approved, failed }

    yield put(AdminBulkApprovePayments.success(result))
    yield put(AdminFetchPendingPayments.request())

    if (action.next) {
      action.next(null, result)
    }
  } catch (error: unknown) {
    captureSentryError(error, { message: '[saga.paymentsReview.onBulkApprovePayments] error' })

    yield put(AdminBulkApprovePayments.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Bulk approve failed'))
    }
  }
}

export function* paymentsReviewSaga() {
  yield takeLatest(AdminFetchPendingPayments.type.REQUEST, onFetchPendingPayments)
  yield takeLatest(AdminFetchPendingPaymentDetails.type.REQUEST, onFetchPendingPaymentDetails)
  yield takeEvery(AdminReviewPayment.type.REQUEST, onReviewPayment)
  yield takeEvery(AdminBulkApprovePayments.type.REQUEST, onBulkApprovePayments)
}
